import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { getLocation } from '$utilities/location';
import { useLocale } from '$contexts/locale.jsx';
import Globe from '$partials/icons/globe';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 */
const LocaleSelector = ({ stylingWhite }) => {
    const {
        current: locale,
        options: localeOptions,
        translate: t,
        formatURI
    } = useLocale();

    const focusTargetRef = useRef();
    const location = getLocation();
    const buttonRef = useRef();

    const [isOpen, setIsOpen] = useState(false);

    const handleBlur = (e) => {
        if (isOpen) {
            const currentTarget = e.currentTarget;

            setTimeout(() => {
                if (!currentTarget.contains(document.activeElement) && document.activeElement !== buttonRef.current) {
                    setIsOpen(false);
                }
            }, 0);
        }
    };

    const open = () => {
        setIsOpen(prevState => {
            const newState = !prevState;
            if (newState) {
                setTimeout(() => focusTargetRef?.current?.focus(), 0);
            }
            return newState;
        });
    };

    return (
        <span className={`locale-span__right-align absolute inline-block type-style-10 ${stylingWhite ? 'text-white' : 'text-orange'}`}>
            <button
                aria-label= {t('Open language select')}
                className='w-[30px] flex flex-row gap-10'
                onClick={open}
                ref={buttonRef}
                type='button'>
                <span>
                    {locale?.labelShort}
                </span>
                <span>
                    <Globe stylingWhite={stylingWhite} />
                </span>
            </button>

            <dialog
                className='top-[30px] left-[-110px] z-50 w-[160px] text-center bg-grey p-0 rounded-button overflow-hidden'
                inert={isOpen ? null : ''}
                onBlur={handleBlur}
                open={isOpen}>
                <ul>
                    {([...localeOptions].filter(l => l.showInSelector).sort((a, b) => b.code === locale.code ? 1 : -1))?.map((option, i) => {
                        return (
                            <li
                                className='block'
                                key={i}>
                                <a
                                    className='block pt-[7px] pb-[8px] px-10 type-style-10 text-black text-left hover:bg-orange hover:text-white'
                                    href={formatURI(location.pathname, option.code)}
                                    ref={i === 0 ? focusTargetRef : null}>
                                    {option.labelLong}
                                </a>
                            </li>
                        );
                    })}
                </ul>
            </dialog>
        </span>
    );
};


/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

LocaleSelector.propTypes = { stylingWhite: PropTypes.bool };

export default LocaleSelector;
